import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const MODES = [["libero", "Nessun limite"], ["unico", "Orario unico"], ["settimanale", "Diverso per giorno"]];
const W = () => ({ start: "08:00", end: "17:00" });
const blank = () => ({ mode: "libero", windows: [W()], types: [],
  days: Object.fromEntries(DAYS.map((_, i) => [String(i), { enabled: i < 5, windows: [W()], types: [] }])) });

export const scheduleSummary = (ws) => {
  if (!ws || ws.mode === "libero") return "orario interventi: nessun limite";
  const win = (r) => r.windows.map((w) => `${w.start}-${w.end}`).join(", ");
  if (ws.mode === "unico") return `orario interventi: ${win(ws)}${ws.types?.length ? ` · ${ws.types.length} tipi` : ""}`;
  return `orario interventi: ${DAYS.filter((_, i) => ws.days?.[i]?.enabled).map((d) => d.slice(0, 3)).join(" ")}`;
};

function Windows({ list, onChange, testid }) {
  const upd = (i, k, v) => onChange(list.map((w, j) => (j === i ? { ...w, [k]: v } : w)));
  return (
    <div className="space-y-2">
      {list.map((w, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`${testid}-window-${i}`}>
          <Input type="time" value={w.start} onChange={(e) => upd(i, "start", e.target.value)} className="w-28" data-testid={`${testid}-start-${i}`} />
          <span className="text-slate-400">–</span>
          <Input type="time" value={w.end} onChange={(e) => upd(i, "end", e.target.value)} className="w-28" data-testid={`${testid}-end-${i}`} />
          {list.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
        </div>
      ))}
      {list.length < 6 && <Button type="button" size="sm" variant="ghost" className="-ml-2" onClick={() => onChange([...list, { start: "14:00", end: "18:00" }])} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /> Aggiungi fascia</Button>}
    </div>
  );
}

function Types({ all, value, onChange, testid }) {
  const tog = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-types`}>
      <button type="button" data-active={!value.length} onClick={() => onChange([])} className="choice rounded-full border bg-white px-3 py-1 text-xs" data-testid={`${testid}-type-all`}>Tutti</button>
      {all.map(([k, l]) => <button key={k} type="button" data-active={value.includes(k)} onClick={() => tog(k)} className="choice rounded-full border bg-white px-3 py-1 text-xs" data-testid={`${testid}-type-${k}`}>{l}</button>)}
    </div>
  );
}

function Rule({ r, all, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Windows list={r.windows} onChange={(windows) => onChange({ ...r, windows })} testid={testid} />
      <p className="text-xs font-medium text-slate-600">Tipi di intervento</p>
      <Types all={all} value={r.types} onChange={(types) => onChange({ ...r, types })} testid={testid} />
    </div>
  );
}

export function TechScheduleDialog({ tech, onClose, onSaved }) {
  const [f, setF] = useState(blank());
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!tech) return;
    const b = blank();
    setF(tech.work_schedule ? { ...b, ...tech.work_schedule, days: { ...b.days, ...tech.work_schedule.days } } : b);
    api.get("/public/config").then((r) => setAll([["sopralluogo", "Sopralluogo"], ...r.data.equipment.map((e) => [e.key, e.label])]));
  }, [tech]);
  const save = async () => {
    setBusy(true);
    try {
      onSaved((await api.put(`/admin/technicians/${tech.id}/schedule`, f)).data);
      toast.success("Orario interventi salvato");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setDay = (i, r) => setF((p) => ({ ...p, days: { ...p.days, [i]: r } }));
  return (
    <Dialog open={!!tech} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="tech-schedule-dialog">
        <DialogHeader><DialogTitle className="font-display">Orario interventi · {tech?.name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {MODES.map(([k, l]) => <button key={k} type="button" data-active={f.mode === k} onClick={() => setF((p) => ({ ...p, mode: k }))}
            className="choice rounded-full border bg-white px-4 py-1.5 text-sm" data-testid={`schedule-mode-${k}`}>{l}</button>)}
        </div>
        <p className="text-xs text-slate-500">Al cliente vengono proposte solo le fasce prenotabili che rientrano negli orari dei tecnici liberi che fanno quel tipo di intervento.</p>
        {f.mode === "unico" && <Rule r={f} all={all} onChange={(r) => setF((p) => ({ ...p, windows: r.windows, types: r.types }))} testid="schedule-unico" />}
        {f.mode === "settimanale" && (
          <div className="divide-y rounded-xl border">
            {DAYS.map((d, i) => {
              const r = f.days[String(i)];
              return (
                <div key={d} className="space-y-2 p-3" data-testid={`schedule-day-${i}`}>
                  <label className="flex items-center justify-between text-sm font-medium">{d}<Switch checked={r.enabled} onCheckedChange={(v) => setDay(String(i), { ...r, enabled: v })} data-testid={`schedule-day-${i}-switch`} /></label>
                  {r.enabled && <Rule r={r} all={all} onChange={(nr) => setDay(String(i), nr)} testid={`schedule-day-${i}`} />}
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full" data-testid="schedule-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva orario</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
